/**
 * Adaptive device tiers — every device gets the smoothest version it can render at 60fps.
 *   high : desktops / strong laptops          -> full Liquid Glass (live blur everywhere, fade-in blur, parallax)
 *   mid  : modern phones & tablets            -> glass only on fixed chrome, transform/opacity animations
 *   low  : weak / old devices, Save-Data      -> no live blur, minimal particles, shorter animations
 * A short runtime FPS probe downgrades the tier if the device can't keep up.
 */
const hasWindow = typeof window !== "undefined";
const nav = typeof navigator !== "undefined" ? navigator : {};
const mq = (q) => hasWindow && Boolean(window.matchMedia) && window.matchMedia(q).matches;
const ua = nav.userAgent || "";
const touchPoints = nav.maxTouchPoints || 0;

const MOBILE_UA = /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile|Silk/i.test(ua);
const IPADOS = /Macintosh/.test(ua) && touchPoints > 1;
const ANDROID = /Android/i.test(ua);

export const IS_TOUCH = MOBILE_UA || IPADOS || mq("(hover: none)") || mq("(pointer: coarse)");

const cores = nav.hardwareConcurrency || 4;
const mem = nav.deviceMemory || (IS_TOUCH ? 4 : 8);
const saveData = Boolean(nav.connection && nav.connection.saveData);

function detectTier() {
  try {
    const forced = hasWindow && window.localStorage.getItem("pk_tier");
    if (forced === "low" || forced === "mid" || forced === "high") return forced;
  } catch (_) {}
  if (saveData || mq("(prefers-reduced-motion: reduce)") || mem <= 2 || cores <= 2 || (ANDROID && (cores <= 4 || mem <= 3))) return "low";
  if (IS_TOUCH || (hasWindow && window.innerWidth < 768) || cores <= 4 || mem <= 4) return "mid";
  return "high";
}

export let TIER = detectTier();
export const LOW_POWER = TIER !== "high";
export const PARTICLE_SCALE = { low: 0.25, mid: 0.6, high: 1 };

function applyClasses(t) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.classList.remove("pk-tier-low", "pk-tier-mid", "pk-tier-high");
  el.classList.add(`pk-tier-${t}`);
  el.classList.toggle("pk-low", t !== "high");
  el.dataset.tier = t;
}
applyClasses(TIER);

const listeners = new Set();
export const onTierChange = (fn) => (listeners.add(fn), () => listeners.delete(fn));

/** ~1s frame-time probe after first paint; downgrade one tier when frames are slow. */
if (hasWindow && window.requestAnimationFrame && TIER !== "low") {
  window.addEventListener(
    "load",
    () => {
      let frames = 0;
      let start = 0;
      let slow = 0;
      let prev = 0;
      const step = (t) => {
        if (!start) start = prev = t;
        const dt = t - prev;
        prev = t;
        if (dt > 34) slow++;
        frames++;
        if (t - start < 1200) return window.requestAnimationFrame(step);
        const ratio = slow / Math.max(1, frames);
        if (ratio > 0.25) {
          TIER = TIER === "high" ? "mid" : "low";
          applyClasses(TIER);
          listeners.forEach((fn) => fn(TIER));
        }
      };
      setTimeout(() => window.requestAnimationFrame(step), 1500);
    },
    { once: true }
  );
}
